import type { ComponentType, FunctionComponent } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useClubMembership } from '../hooks/useClubMembership'
import { MEMBERSHIP_BILLING_ENABLED } from '../lib/membership'
import { RedirectOnce } from './RedirectOnce'
// The spinner ClubhouseLayout already draws while a standing loads (App.tsx)
// — the two gates must look the same mid-load. This file lives beside
// RedirectOnce rather than in components/golf because it is routing glue,
// not club kit: a club screen may never import from components/ui, and
// keeping the wrapper out of the golf barrel keeps that true.
import { Spinner } from './ui/Spinner'

/**
 * Members in standing see no payment material.
 *
 * The homepage's join rail, the terms and dues on /membership, the
 * application and the checkout all exist to SELL a standing. A member who
 * already holds one must never be shown them — not as a courtesy, but because
 * every one of those pages ends in "pay", and the only honest thing the club
 * can say to a member in standing is "you are in". So the wrapped screen is
 * swapped for a redirect to the clubhouse (or the account page) once the
 * standing is known to be active.
 *
 * Why `active`, and not the tier: a GRANTED standing (a Quartermaster) is
 * active and complimentary with no Stripe row behind it, and is in standing
 * exactly like a paid one; while a lapsed (`past_due`) or never-settled
 * (`incomplete`) member is NOT, and still needs these pages — the terms, the
 * dues, the checkout — in front of them. `active` is the one bit that says
 * "in standing" for every tier and every way of getting there.
 *
 * The order of checks mirrors ClubhouseLayout, with the same three refusals
 * to over-reach: a visitor and a billing-dark club see the page as-is (there
 * is no standing to redirect on), and nothing is decided until the standing
 * has loaded — deciding early would flash the join rail at a paying member on
 * every cold load and then yank it away. Unlike the clubhouse gate there is no
 * fail-open branch to write: a failed fetch leaves `active` unknown, and an
 * unknown standing gets the page, which is already the permissive answer here.
 *
 * RedirectOnce, not Navigate: the target lives OUTSIDE this route's subtree
 * (the clubhouse is a sibling layout), which is exactly the shape that loops
 * <Navigate> into React #185. See RedirectOnce for the mechanism.
 *
 * Returns a FunctionComponent rather than the wider ComponentType: TanStack's
 * route `component` takes a callable, and a class component is not one.
 */
export function withStandingRedirect(Screen: ComponentType, to: string): FunctionComponent {
  function StandingRedirect() {
    const { isAuthenticated } = useAuth()
    const { membership, isLoading } = useClubMembership()

    if (!isAuthenticated || !MEMBERSHIP_BILLING_ENABLED) return <Screen />

    if (isLoading) {
      return (
        <div className="flex h-screen items-center justify-center bg-paper">
          <Spinner size="lg" className="text-pine" />
        </div>
      )
    }

    if (membership?.active) return <RedirectOnce to={to} />

    return <Screen />
  }
  StandingRedirect.displayName = `withStandingRedirect(${Screen.displayName ?? Screen.name})`
  return StandingRedirect
}
